import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import { intersection } from "lodash";

import { useAppSelector } from "app/hooks";
import { ACCESS_TO_COSTS_ENABLED } from "constants/feature-flags";
import {
  ADMIN_LEVEL_GROUPS,
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SCHEDULER,
  GROUP_SUNO
} from "constants/staff";
import { selectCurrentUser } from "features/auth/auth-slice";

export default function useUserRoles() {
  const currentUser = useAppSelector(selectCurrentUser);

  const userRoles = useMemo(() => {
    return currentUser?.groups?.map(({ name }) => name) || [];
  }, [currentUser?.groups]);

  const userPermissions: { [permissionName: string]: boolean } = useMemo(() => {
    const permissions = {};
    currentUser?.groups?.reduce(
      (permissionsDict: { [permissionName: string]: boolean }, group) => {
        group.permissions.forEach(({ codename }) => {
          permissionsDict[codename] = true;
        });
        return permissionsDict;
      },
      permissions
    );
    return permissions;
  }, [currentUser?.groups]);

  const hasRole = useCallback(
    (role: string[] | string) => {
      return (
        intersection(Array.isArray(role) ? role : [role], userRoles).length > 0
      );
    },
    [userRoles]
  );

  const isSunoAdminOrBusinessOwner = useMemo(
    () => hasRole([GROUP_SUNO, GROUP_BUSINESS_OWNERS]),
    [hasRole]
  );

  const isAdminOrBusinessOwner = useMemo(
    () => hasRole(ADMIN_LEVEL_GROUPS),
    [hasRole]
  );

  const isBusinessOwner = useMemo(
    () => hasRole([GROUP_BUSINESS_OWNERS]),
    [hasRole]
  );

  const isSunoAdmin = useMemo(() => hasRole([GROUP_SUNO]), [hasRole]);
  const isAudiologist = useMemo(() => hasRole([GROUP_AUDIOLOGISTS]), [hasRole]);
  const isScheduler = useMemo(() => hasRole([GROUP_SCHEDULER]), [hasRole]);

  const flags = useFlags();
  const accessToCostsEnabled = flags[ACCESS_TO_COSTS_ENABLED];
  const canViewCosts = accessToCostsEnabled
    ? Boolean(currentUser?.access_to_costs)
    : Boolean(currentUser?.can_see_manufacturer_cost);

  const canAccessDashboards = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.has_access_to_dashboards),
    [hasRole, currentUser?.has_access_to_dashboards]
  );

  const canAccessQBO = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.has_access_to_qbo),
    [hasRole, currentUser?.has_access_to_qbo]
  );

  const canAccessCampaigns = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.can_view_and_create_campaigns),
    [hasRole, currentUser?.can_view_and_create_campaigns]
  );

  const canViewCatalog = useMemo(
    () =>
      isAdminOrBusinessOwner ||
      Boolean(currentUser?.can_view_catalog) ||
      Boolean(currentUser?.can_edit_catalog),
    [
      isAdminOrBusinessOwner,
      currentUser?.can_view_catalog,
      currentUser?.can_edit_catalog
    ]
  );

  const canEditCatalog = useMemo(
    () => isAdminOrBusinessOwner || Boolean(currentUser?.can_edit_catalog),
    [isAdminOrBusinessOwner, currentUser?.can_edit_catalog]
  );

  const memoizedCurrentUser = useMemo(() => {
    if (!currentUser) return null;
    return { ...currentUser, is_active: true };
  }, [currentUser]);

  return {
    isSunoAdminOrBusinessOwner,
    isAdminOrBusinessOwner,
    hasRole,
    userRoles,
    userId: currentUser?.id,
    isSunoAdmin,
    isBusinessOwner,
    userPermissions,
    currentUser: memoizedCurrentUser,
    isAudiologist,
    isScheduler,
    canViewCosts,
    canAccessDashboards,
    canAccessQBO,
    canAccessCampaigns,
    canViewCatalog,
    canEditCatalog
  };
}
